import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

import config from "@/config";
import { ANALYTICS_USER_TYPES, orgAnalytics, ORGANIZATION_ANALYTICS_EVENTS } from "@/helpers/analytics";
import { get } from "@/helpers/http";
import log from "@/helpers/log";
import { ROLE, TEAM_MODULE_KEY, TEAM_PERMISSION } from "@/utils/enums";
import { Team, TeamInvite, TeamMember } from "@/utils/interface";
import { hasPermission } from "@/utils/team";
import store from "@/vuexStore";

import { authDelete, authGet, authPatch, authPost } from "../authStoreRequest";
import billingModule from "./billing";
import userModule from "./user";

// @ts-expect-error decorator is picking the wrong overload as its type
@Module({
  namespaced: true,
  name: TEAM_MODULE_KEY,
  store,
  dynamic: true,
})
class TeamModule extends VuexModule {
  teams: Array<Team> = [];

  teamMembers: Record<number, Array<TeamMember>> = {};

  teamInvites: Record<number, Array<TeamInvite>> = {};

  public get teamsGetter() {
    return this.teams;
  }

  public get ownerTeamsGetter() {
    return this.teams.filter((team) => team.role === ROLE.owner);
  }

  public get selectedTeamGetter() {
    return (teamName: string) => this.teams?.find((team) => team.name === teamName) ?? ({} as Team);
  }

  public get selectedTeamById() {
    return (teamId: number) => this.teams?.find((team) => team.id === teamId) ?? ({} as Team);
  }

  public get teamMembersGetter() {
    return (teamName: string) => {
      const team = this.selectedTeamGetter(teamName);
      return this.teamMembers[team.id];
    };
  }

  @Mutation
  public setTeams(teams: Array<Team>) {
    this.teams = teams;
  }

  @Mutation
  public setTeamMembers({ teamId, teamMembers }: { teamId: number; teamMembers: Array<TeamMember> }) {
    this.teamMembers[teamId] = teamMembers;
  }

  @Mutation
  public setInvites({ invites, teamId }: { invites: TeamInvite[]; teamId: number }) {
    this.teamInvites[teamId] = invites;
  }

  @Mutation
  public removeInvite({ teamId, inviteId }: { teamId: number; inviteId: number }) {
    const currentTeamInvites = this.teamInvites[teamId];
    const idx = currentTeamInvites.findIndex((i: TeamInvite) => i.ID === inviteId);
    currentTeamInvites.splice(idx, 1);
    this.teamInvites[teamId] = currentTeamInvites;
  }

  @Mutation
  public spliceTeamMember({ teamId, teamMemberId }: { teamId: number; teamMemberId: number }) {
    const currentTeamMembers = this.teamMembers[teamId];
    const teamMemberIndex = currentTeamMembers.findIndex((member: TeamMember) => member.ID === teamMemberId);
    currentTeamMembers.splice(teamMemberIndex, 1);
    this.teamMembers[teamId] = currentTeamMembers;
  }

  @Mutation
  public spliceTeam({ teamId }: { teamId: number }) {
    const teamIndex = this.teams.findIndex((team) => team.id === teamId);
    this.teams.splice(teamIndex, 1);
    delete this.teamMembers[teamId];
  }

  @Action
  async getTeams() {
    try {
      const response = await authGet(`${config.backendURL}/teams`, {}, {});
      const { teams = [] } = response as { teams: Array<Team> };
      this.setTeams(teams);
    } catch (error) {
      log.error("fail to get teams", error);
      throw error;
    }
  }

  @Action
  async fetchTeamMembers({ teamId }: { teamId: number }) {
    try {
      const response = await authGet(`${config.backendURL}/teams/${teamId}/members`, {}, {});
      const { team_members: teamMembers } = response as { team_members: Array<TeamMember> };
      this.setTeamMembers({ teamId, teamMembers });
    } catch (error) {
      log.error("fail to get team members", error);
      throw error;
    }
  }

  @Action
  async createOrganization({
    displayName,
    industry,
    organizationSize,
    organizationUrl,
  }: {
    displayName: string;
    industry: string;
    organizationSize: string;
    organizationUrl: string;
  }): Promise<{ id: number; name: string }> {
    try {
      const data = { display_name: displayName, industry, organization_size: organizationSize, organization_url: organizationUrl };
      const response = (await authPost(`${config.backendURL}/teams`, data, {})) as { id: number; name: string };

      // analytics
      orgAnalytics.identify(response.id.toString(), {
        name: response.name,
        team_id: response.id,
        team_size: organizationSize,
        team_url: organizationUrl,
        team_industry: industry,
        type: ANALYTICS_USER_TYPES.ORGANIZATION,
      });
      orgAnalytics.setGlobalProperties({ team_id: response.id });
      const { authConnection } = userModule.userInfoGetter || {};
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.ORGANIZATION_CREATED, {
        team_id: response.id,
        team_name: displayName,
        team_size: organizationSize,
        team_url: organizationUrl,
        team_industry: industry,
      });
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.MEMBER_JOINED, {
        user_id: userModule.userProfileGetter.ID,
        user_auth_connection: authConnection,
        user_type_of_login: userModule.typeOfLogin,
        user_mfa_enabled: false,
        user_role: ROLE.owner,
      });

      await this.getTeams();
      return response;
    } catch (error) {
      log.error("failed to create new organization", error);
      throw error;
    }
  }

  @Action
  async updateTeam({
    teamId,
    displayName,
    industry,
    organizationSize,
    organizationUrl,
    description,
  }: {
    teamId: number;
    displayName: string;
    industry: string;
    organizationSize: string;
    organizationUrl: string;
    description?: string;
  }) {
    try {
      const data = { display_name: displayName, industry, organization_size: organizationSize, organization_url: organizationUrl, description };
      await authPatch(`${config.backendURL}/teams/${teamId}`, data, {});

      // analytics
      orgAnalytics.identify(teamId.toString(), {
        name: displayName,
        team_id: teamId,
        team_size: organizationSize,
        team_url: organizationUrl,
        team_industry: industry,
        type: ANALYTICS_USER_TYPES.ORGANIZATION,
      });
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.ORGANIZATION_UPDATED, {
        team_id: teamId,
        team_name: displayName,
        team_size: organizationSize,
        team_url: organizationUrl,
        team_industry: industry,
      });

      await this.getTeams();
    } catch (error) {
      log.error("fail to update team", error);
      throw error;
    }
  }

  @Action
  async sendInvite({ teamId, email, role }: { teamId: number; email: string; role: string }) {
    try {
      const response = await authPost(`${config.backendURL}/teams/${teamId}/invites`, { email, role }, {});

      // analytics
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.MEMBER_INVITE_SENT, {
        member_role: role,
      });

      const { success } = response as { success: boolean };
      if (success) {
        log.info("invite has been sent");
      }
    } catch (error) {
      log.error("fail to send invite to email address", error);
      throw error;
    }
  }

  @Action
  async getInvites({ teamId }: { teamId: number }) {
    try {
      const response = await authGet(`${config.backendURL}/teams/${teamId}/invites`, {}, {});
      const { invites } = response as { invites: Array<TeamInvite> };
      this.setInvites({ invites, teamId });
    } catch (error) {
      log.error("fail to get team invite codes", error);
      throw error;
    }
  }

  @Action
  async getInvite({ inviteCode }: { inviteCode: string }) {
    try {
      const response = await get(`${config.backendURL}/teams/inviteByCode`, { invite_code: inviteCode }, {});
      const { invite } = response as { invite: TeamInvite };
      return invite;
    } catch (error) {
      log.error("fail to get team invite", error);
      throw error;
    }
  }

  @Action
  async replyInvite({ inviteCode, accepted }: { inviteCode: string; accepted: boolean }) {
    try {
      await authPost(`${config.backendURL}/teams/replyInvite`, { invite_code: inviteCode, accepted }, {});
      if (accepted) {
        await this.getTeams();
      }
    } catch (error) {
      log.error("fail to reply to team invite", error);
      throw error;
    }
  }

  @Action
  async cancelInvite({ teamId, inviteId }: { teamId: number; inviteId: number }) {
    try {
      const data = {
        invite_id: String(inviteId),
      };
      await authDelete(`${config.backendURL}/teams/${teamId}/invites`, data, {});

      // analytics
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.MEMBER_INVITE_CANCELLED);

      this.removeInvite({ teamId, inviteId });
    } catch (error) {
      log.error("fail to cancel invitation", error);
      throw error;
    }
  }

  @Action
  async updateTeamMember({ teamId, role, teamMemberId }: { teamId: number; role: string; teamMemberId: number }) {
    try {
      const data = { role, team_member_id: teamMemberId };
      await authPatch(`${config.backendURL}/teams/${teamId}/members`, data, {});

      // analytics
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.MEMBER_ROLE_UPDATED, {
        member_id: teamMemberId,
        new_role: role,
      });

      await this.fetchTeamMembers({ teamId });
      if (hasPermission(role, TEAM_PERMISSION.billing)) {
        billingModule.fetchSubscription({ teamId });
      }
    } catch (error) {
      log.error("fail to update team member", error);
      throw error;
    }
  }

  @Action
  async transferTeamOwnership({ teamId, teamMemberId }: { teamId: number; teamMemberId: number }) {
    try {
      const data = { team_member_id: teamMemberId };
      await authPatch(`${config.backendURL}/teams/${teamId}/members/transferOwnership`, data, {});

      // analytics
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.ORGANIZATION_OWNER_TRANSFERRED, {
        new_owner_id: teamMemberId,
      });

      await this.getTeams();
      await this.fetchTeamMembers({ teamId });
    } catch (error) {
      log.error("fail to update team ownership", error);
      throw error;
    }
  }

  @Action
  async removeTeamMember({ teamId, teamMemberId }: { teamId: number; teamMemberId: number }) {
    try {
      const requestParams: Record<string, string> = { team_member_id: String(teamMemberId) };
      await authDelete(`${config.backendURL}/teams/${teamId}/members`, requestParams, {});

      // analytics
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.MEMBER_REMOVED, {
        member_id: teamMemberId,
      });

      await this.getTeams();
      this.spliceTeamMember({ teamId, teamMemberId });
    } catch (error) {
      log.error("fail to remove team member", error);
      throw error;
    }
  }

  @Action
  async leaveTeam({ teamId }: { teamId: number }) {
    try {
      await authDelete(`${config.backendURL}/teams/${teamId}/members/leaveTeam`, {}, {});

      // analytics
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.MEMBER_LEFT);

      this.spliceTeam({ teamId });
    } catch (error) {
      log.error("fail to leave team", error);
      throw error;
    }
  }

  @Action
  async deleteTeam({ teamId }: { teamId: number }) {
    try {
      await authDelete(`${config.backendURL}/teams/${teamId}`, {}, {});

      // analytics
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.ORGANIZATION_DELETED);
      orgAnalytics.identify(teamId.toString(), {
        is_deleted: true,
      });

      this.spliceTeam({ teamId });
    } catch (error) {
      log.error("fail to delete team", error);
      throw error;
    }
  }
}

const teamModule = getModule(TeamModule);

export default teamModule;
